import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const ConcentrationAnalysis = ({ data, title, type = "sector" }) => {
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];

  // Safety: ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-popover-foreground">{data?.name}</p>
          <p className="text-sm text-muted-foreground">
            Allocation: {data?.value}% (₹{data?.amount?.toLocaleString('en-IN')})
          </p>
          {data?.riskLevel && (
            <p className={`text-sm ${
              data?.riskLevel === 'high' ? 'text-error' :
              data?.riskLevel === 'medium' ? 'text-warning' : 'text-success'
            }`}>
              Risk: {data?.riskLevel}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Safe max calculation that handles empty arrays
  const getMaxConcentration = () => {
    if (safeData.length === 0) return 0;
    const values = safeData.map(item => item?.value || 0).filter(v => isFinite(v));
    return values.length > 0 ? Math.max(...values) : 0;
  };

  const getConcentrationWarning = () => {
    const maxConcentration = getMaxConcentration();
    if (maxConcentration > 30) return { level: 'high', message: 'High concentration risk detected' };
    if (maxConcentration > 20) return { level: 'medium', message: 'Moderate concentration risk' };
    return { level: 'low', message: 'Well diversified portfolio' };
  };

  const warning = getConcentrationWarning();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs ${
          warning?.level === 'high' ? 'bg-error/10 text-error' :
          warning?.level === 'medium'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
        }`}>
          <Icon 
            name={warning?.level === 'high' ? 'AlertTriangle' : warning?.level === 'medium' ? 'AlertCircle' : 'CheckCircle'} 
            size={14} 
          />
          <span>{warning?.message}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={safeData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {safeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-3">
          {safeData.map((item, index) => (
            <div key={item?.name} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center space-x-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                ></div>
                <div>
                  <div className="font-medium text-foreground">{item?.name}</div>
                  {item?.count && (
                    <div className="text-xs text-muted-foreground">{item?.count} positions</div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="font-medium text-foreground">{item?.value}%</div>
                {item?.amount && (
                  <div className="text-xs text-muted-foreground">
                    ₹{item?.amount?.toLocaleString('en-IN')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Largest Position:</span>
            <span className="ml-2 font-medium text-foreground">
              {getMaxConcentration()}%
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Diversification:</span>
            <span className="ml-2 font-medium text-foreground">{safeData.length} {type}s</span>
          </div>
          <div>
            <span className="text-muted-foreground">HHI Index:</span>
            <span className="ml-2 font-medium text-foreground">
              {Math.round(safeData.reduce((sum, item) => sum + Math.pow(item?.value || 0, 2), 0))}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Risk Score:</span>
            <span className={`ml-2 font-medium ${
              warning?.level === 'high' ? 'text-error' :
              warning?.level === 'medium' ? 'text-warning' : 'text-success'
            }`}>
              {warning?.level === 'high' ? 'High' : warning?.level === 'medium' ? 'Medium' : 'Low'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConcentrationAnalysis;